import { recaptchaConfig } from "@/config/recaptcha";
import type { RecaptchaVerificationRequest, RecaptchaVerificationResponse } from "@/types/recaptcha";
import { demoFormUtils } from "@/pages/demo/demo.utils";

export class RecaptchaService {
  async executeToken(action: string): Promise<string> {
    try {
      return new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
          reject(new Error("reCAPTCHA not available"));
          return;
        }

        window.grecaptcha.ready(() => {
          window.grecaptcha
            ?.execute?.(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action })
            .then(resolve)
            .catch(reject);
        });
      });
    } catch (error) {
      demoFormUtils.track("recaptcha_token_execution_error", {
        action
      });

      throw error;
    }
  }

  async verifyToken({
    token,
    action,
    email
  }: {
    token: string;
    action: string;
    email: string;
  }): Promise<RecaptchaVerificationResponse> {
    try {
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/recaptcha/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token,
          action,
          email
        } as RecaptchaVerificationRequest)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      demoFormUtils.track("recaptcha_verification_error", {
        action
      });

      return RecaptchaService.createRecaptchaFailure(error instanceof Error ? error.message : String(error));
    }
  }

  async executeAndVerify({ action, email }: { action: string; email: string }): Promise<RecaptchaVerificationResponse> {
    const startTime = performance.now();

    try {
      const token = await this.executeToken(action);

      const result = await this.verifyToken({ token, action, email });
      const executionTime = performance.now() - startTime;

      demoFormUtils.track("recaptcha_execution_completed", {
        action,
        execution_time_ms: Math.round(executionTime),
        success: result.success,
        score: result.recaptcha_v3_score,
        status: result.recaptcha_status
      });

      return result;
    } catch (error) {
      const executionTime = performance.now() - startTime;

      demoFormUtils.track("recaptcha_execution_failed", {
        action,
        execution_time_ms: Math.round(executionTime)
      });

      return RecaptchaService.createRecaptchaFailure(error instanceof Error ? error.message : String(error));
    }
  }

  static createRecaptchaFailure = (errorMessage?: string): RecaptchaVerificationResponse => ({
    success: false,
    recaptcha_v3_score: -1,
    recaptcha_status: "degraded",
    error: errorMessage
  });
}

export const recaptchaService = new RecaptchaService();
